import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../services/api/axiosConfig';
import { TextInput, Textarea, Button, Spinner, Alert } from 'flowbite-react'; // Import Alert for error display
import Label from '../../../components/Label';
import errorHandler from '../../../utils/errorHandler';
import ShowValidationMessage from '../../../components/ShowValidationMessage';
import Breadcrumb from '../../dashboard/pages/components/BreadCrumb';
import PageHeading from '../../../components/PageHeading';
import {toast} from "react-toastify";

// Import ReactQuill and its styles
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // You can choose 'quill.bubble.css' if you prefer


const SendCustomerEmail = () => {
    const [formData, setFormData] = useState({
        subject: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams(); // Get the customer ID from the URL

    // New states for customer validation
    const [customerDetails, setCustomerDetails] = useState(null);
    const [loadingCustomer, setLoadingCustomer] = useState(true);
    const [customerError, setCustomerError] = useState(null);

    useEffect(() => {
        document.title = 'Send mail to customer';
    }, []);

    // Effect to fetch customer details on component mount or id change
    useEffect(() => {
        const fetchDetails = async () => {
            setLoadingCustomer(true);
            setCustomerError(null); // Clear previous errors
            try {
                const response = await api.get(`/customer/${id}`);
                if (response.data.success && response.data.data) {
                    setCustomerDetails(response.data.data);
                    document.title = `Send mail to ${response.data.data.full_name}`;
                } else {
                    // If API responds but success is false or data is missing
                    setCustomerError(response.data.message || 'Customer not found.');
                }
            } catch (err) {
                console.error('Error fetching customer details:', err);
                if (err.response) {
                    // Specific error for 404 Not Found, etc.
                    setCustomerError(err.response.data.message || `Failed to load customer details. Status: ${err.response.status}`);
                } else if (err.request) {
                    setCustomerError('Network Error: No response from server when fetching customer details.');
                } 
            } finally {
                setLoadingCustomer(false);
            }
        };

        if (id) {
            fetchDetails();
        } else {
            setLoadingCustomer(false);
            setCustomerError('Customer ID is missing from the URL.');
        }
    }, [id]); // Dependency on 'id'

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setErrors(prev => ({
            ...prev,
            [name]: '',
            general: '' // Clear general error when user types
        }));
        setSuccessMessage(''); // Clear success message when user types
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.subject.trim()) {
            newErrors.subject = 'Subject is required.';
        }

         // Quill's empty content is typically '<p><br></p>' or ''
        const isEmptyQuillContent = !formData.message || formData.message.trim() === '' || formData.message.trim() === '<p><br></p>';

        if (isEmptyQuillContent) {
            newErrors.message = 'Message is required.';
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleQuillChange = (content, delta, source, editor) => {
        // 'content' is the HTML string from the editor
        setFormData(prev => ({
            ...prev,
            message: content
        }));
        setErrors(prev => ({
            ...prev,
            message: '', // Clear message error when user types in editor
            general: ''
        }));
        setSuccessMessage('');
    };


     // Quill modules configuration (toolbar options)
    const quillModules = {
        toolbar: [
            [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
            [{size: []}],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}, 
             {'indent': '-1'}, {'indent': '+1'}],
            ['link'],
            ['clean'] // remove formatting button
        ],
    };

    // Quill formats (allowed HTML tags)
    const quillFormats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent','link'
    ];


    const breadcrumbItems = [
        { label: 'Dashboard', url: '/dashboard' },
        { label: 'Customers', url: '/dashboard/customer-list' },
        { label: 'Send email', url: '#', current: true }
    ];


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        setSuccessMessage('');

        if (validateForm()) {
            try {
                // Ensure customerDetails is available before sending email
                if (!customerDetails) {
                    throw new Error("Customer details not loaded, cannot send email.");
                }

                const response = await api.post('customer/'+id+'/mail', formData);
                setFormData({ 
                    subject: '',
                    message: ''
                });
                // console.log('Email sent successfully:', response.data);
                toast.success("Email sent successfully!")
               
                navigate('/dashboard/customer-email'); // Uncomment if you want to redirect after success
            } catch (error) {
                console.error('Failed to send email:', error);
                errorHandler(error, setErrors);
                setSuccessMessage('');
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            console.log('Client-side validation failed.');
            setSuccessMessage('');
        }
    };

    // --- Conditional Rendering based on customer details fetch status ---
    if (loadingCustomer) {
        return (
            <div className="min-h-screen bg-gray-100 p-6 flex justify-center items-center">
                <Spinner size="xl" /> <span className="ml-3 text-lg ">Loading Customer Details...</span>
            </div>
        );
    }

    if (customerError) {
        return (
            <div className="min-h-screen bg-gray-100 p-6 flex justify-center items-center flex-col">
                <Alert color="failure" onDismiss={() => setCustomerError(null)} className="mb-4">
                    <span className="font-medium">Error:</span> {customerError}
                </Alert>
                <Button onClick={() => navigate('/dashboard/customer-list')} className="bg-blue-500 hover:bg-blue-600">
                    Go to Customer List
                </Button>
            </div>
        );
    }

    if (!customerDetails) {
        // This case might be covered by customerError, but as a fallback
        return (
            <div className="min-h-screen bg-gray-100 p-6 text-center flex justify-center items-center flex-col">
                <h2 className="text-2xl font-bold mb-4">Customer Not Found</h2>
                <p className="mb-6">The customer you are trying to send an email to does not exist.</p>
                <Button onClick={() => navigate('/dashboard/customer-list')} className="bg-blue-500 hover:bg-blue-600">
                    Go to Customer List
                </Button>
            </div>
        );
    }

    // If we reach here, customerDetails are loaded successfully
    return (
        <div className="p-4 sm:p-6 lg:p-8">

            <Breadcrumb items={breadcrumbItems} />

            {/* Display customer's name in the heading */}
            <PageHeading value={`Send Email to ${customerDetails.full_name}`} />

            <form onSubmit={handleSubmit} className="space-y-6">
                {errors.general && (
                    <div className="p-3 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
                        {errors.general}
                    </div>
                )}

                {successMessage && (
                    <div className="p-3 text-sm text-green-700 bg-green-100 rounded-lg" role="alert">
                        {successMessage}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <Label htmlFor="subject" label="Subject" />
                        <TextInput
                            id="subject"
                            name="subject"
                            type="text"
                            autoComplete="subject"
                            required
                            value={formData.subject}
                            onChange={handleChange}
                            className="placeholder-gray-500"
                            placeholder="Subject"
                            aria-invalid={errors.subject ? "true" : "false"}
                            aria-describedby="subject-error"
                            disabled={loading}
                        />
                        <ShowValidationMessage error={errors.subject} />
                    </div>
                </div>

                <div className="md:col-span-2">
                    <Label htmlFor="message" label="Message" />
                    {/* ReactQuill replaces Textarea */}
                    <ReactQuill
                        theme="snow"
                        value={formData.message}
                        onChange={handleQuillChange}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Please write your mail here"
                        style={{ height: '300px', marginBottom: '50px' }} // Adjusted height and added bottom margin
                        className={errors.message ? "border border-red-500 rounded-lg" : ""} // Apply error styling
                    />
                    <ShowValidationMessage error={errors.message} />

                </div>

                <div className="pt-2">
                    <Button type="submit" disabled={loading} className='ml-auto' >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="mr-3" />
                                Sending
                            </>
                        ) : (
                            <span>Send</span>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default SendCustomerEmail;